import { useState } from 'react';
import { Typography, Paper, Stack, IconButton, Divider, Box } from '@mui/material';
import ArrowCircleDownOutlined from '@mui/icons-material/ArrowCircleDownOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useTranslation } from 'react-i18next';
import { formatPriceString } from '@/utils/numbers';
import { ReferenceDetailsProps } from '../types';

const ReferenceDetails = ({ invoice }: ReferenceDetailsProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <Stack
      gap={2}
      sx={{
        width: '100%'
      }}
    >
      <Typography component='h2' variant='h2' fontWeight='bold'>
        {t('common-phrases.reference')}
      </Typography>
      <Paper>
        <Stack>
          <Stack sx={{ padding: 3 }} direction='row' justifyContent='space-between' alignItems='center'>
            <Typography variant='body1'>{invoice.referenceCode}</Typography>
            <CheckCircleIcon color='success' fontSize='medium' />
          </Stack>

          <Box
            sx={{
              maxHeight: expanded ? '250px' : '0px',
              overflowY: invoice?.invoiceItems.length > 4 ? 'scroll' : 'hidden',
              transition: 'max-height 0.3s ease-out, margin-bottom 0.3s ease-out',
              marginBottom: expanded ? 2 : 0
            }}
          >
            <Stack
              gap={1}
              sx={{
                paddingX: 3,
                paddingY: 2
              }}
            >
              {invoice &&
                invoice.invoiceItems.map((item, index) => (
                  <Stack key={index} direction='row' justifyContent='space-between'>
                    <Typography variant='body2'>{item.description}</Typography>
                    <Typography variant='body2'>{formatPriceString(item.amount)}</Typography>
                  </Stack>
                ))}
            </Stack>
          </Box>

          <Box
            sx={{
              position: 'relative',
              height: '1px',
              display: 'flex',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <Divider
              textAlign='right'
              sx={{
                width: '100%',
                position: 'relative',
                '& .MuiDivider-wrapper': {
                  position: 'absolute',
                  right: '24px',
                  top: '-13px',
                  paddingLeft: 0,
                  paddingRight: 0
                },
                '& .MuiButtonBase-root': {
                  padding: 0
                }
              }}
            >
              <IconButton
                size='small'
                sx={{
                  transform: expanded ? 'translateY(-1px) rotate(180deg)' : 'translateY(0px) rotate(0deg)',
                  transition: 'transform 0.3s ease-out, top 0.3s ease-out',
                  zIndex: 1,
                  backgroundColor: '#fff',
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
                onClick={toggleExpanded}
              >
                <ArrowCircleDownOutlined fontSize='medium' />
              </IconButton>
            </Divider>
          </Box>

          <Box sx={{ padding: 3 }}>
            <Stack gap={2}>
              <Stack direction='row' justifyContent='space-between' alignItems='center'>
                <Typography variant='body2' fontWeight='bold'>
                  {t('components.payment-option-selector.total-amount')}
                </Typography>
                <Typography variant='body2' fontWeight='bold'>
                  {formatPriceString(invoice.amountInclVAT)}
                </Typography>
              </Stack>
              {invoice?.status?.toLowerCase() === 'partiallypaid' && (
                <Stack direction='row' justifyContent='space-between' alignItems='center'>
                  <Typography variant='body2' fontWeight='bold'>
                    {t('components.payment-option-selector.remaining-amount')}
                  </Typography>
                  <Typography variant='body2' fontWeight='bold'>
                    {formatPriceString(invoice.remainingAmountInclVAT)}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Stack>
  );
};

export default ReferenceDetails;
